import { CompanyBenefitsResponse } from './company-benefit';
import { CustomLinksAttributes, MultiLinksResponse } from './custom-links';
import { FollowingResponse } from './following';
import { InsightArticleRequest, InsightsResponse } from './insight';
import { MetaTagsResponse } from './meta-tag';
import { NationalitiesResponse } from './nationality';
import { TStatRequest, TStatableType, TStatsResponse } from './stat';
import { BasicStudentSocietiesResponse } from './student-society';
import { TagResponse, TagsResponse, TagType } from './tag';
import { TouchpointType } from './touchpoint';

export const PER_PAGE_COUNT = 20;

export interface CompanyAttributes {
  unseen_count: number;
  id: string;
  name: string;
  icon_url: string;
  banner_url: string;
  address: string;
  description: string;
  onboarded: boolean;
  instagram_url: string;
  facebook_url: string;
  linkedin_url: string;
  twitter_url: string;
  website: string;
  men: number;
  women: number;
  lgbtq: number;
  company_benefits: CompanyBenefitsResponse;
  nationalities: NationalitiesResponse;
  insights: InsightsResponse;
  created_at: string;
  updated_at: string;
  slug: string;
  meta_title: string;
  meta_description: string;
  meta_tags: MetaTagsResponse;
  size: string;
  founded_year: number;
  following: FollowingResponse | null;
  is_verified: boolean;
  entityId?: string;
  short_description: string;
  employees_count: string;
  sanitized_description: string;
  industry: TagResponse<TagType.Industry>;
  sub_industries: TagsResponse<TagType.Industry>;
  company_size: { data: CompanySize };
  live_count_hash: {
    Event: number;
    Job: number;
    Internship: number;
  };
  authenticated?: boolean;
  career_page_link?: string;
  huzzle_verified: boolean;
  public_url?: string;
  custom_industry_name: string;
  custom_links: MultiLinksResponse;
  sponsored_societies: BasicStudentSocietiesResponse;
  stats: TStatsResponse<TStatableType.Company, 'stat', 'Stat'>;
  culture_stats: TStatsResponse<
    TStatableType.Company,
    'culture_stat',
    'CultureStat'
  >;
  benefit_stats: TStatsResponse<
    TStatableType.Company,
    'benefit_stat',
    'BenefitStat'
  >;
  achievement_stats: TStatsResponse<
    TStatableType.Company,
    'achievement_stat',
    'AchievementStat'
  >;
  goal_stats: TStatsResponse<TStatableType.Company, 'goal_stat', 'GoalStat'>;
}

export type TCompany = CompanyAttributes;

export interface Company {
  id: string;
  type: 'company';
  attributes: CompanyAttributes;
}

export type TBasicCompany = Pick<
  CompanyAttributes,
  'id' | 'name' | 'slug' | 'icon_url'
> & { industry_name: string };

export interface BasicCompany {
  id: string;
  type: 'basic_company';
  attributes: TBasicCompany;
}

export interface CompanyResponse {
  data: Company;
}

export interface CompaniesResponse {
  data: Array<Company>;
  meta: {
    total: number;
  };
}

export interface BasicCompaniesResponse {
  data: Array<BasicCompany>;
  meta: {
    total: number;
  };
}

export interface CompanyRequest {
  name: string;
  description: string;
  short_description: string;
  address: string;
  instagram_url: string;
  facebook_url: string;
  linkedin_url: string;
  website: string;
  men: number;
  women: number;
  lgbtq: number;
  logo: FileList;
  banner: FileList;
  remove_banner?: boolean;
  founded_year: string;
  industry_id: string;
  sub_industry_id: string;
  employees_count: string;
  insights_attributes: Array<InsightArticleRequest>;
  custom_industry_name: string;
  custom_links_attributes: Array<CustomLinksAttributes>;
  culture_stats_attributes: Array<TStatRequest>;
  benefit_stats_attributes: Array<TStatRequest>;
}

export interface CompanySizeAttributes {
  id: string;
  label: string;
  value: string;
  name: string;
}

export interface CompanySize {
  id: string;
  type: 'company_size';
  attributes: CompanySizeAttributes;
}

export interface CompanySizeResponse {
  data: Array<CompanySize>;
  meta: {
    total: number;
  };
}

export interface IndustryAttributes {
  id: string;
  name: string;
  position: number;
  slug: string;
  type: string;
}

export interface Industry {
  id: string;
  type: 'tag';
  attributes: IndustryAttributes;
}

export interface IndustryResponse {
  data: Array<Industry>;
}

export interface AvatarURLsAttributes {
  avatar_url: string;
  email: string;
  name: string;
}

export interface CompanyDashboardAttributes {
  live_non_event_touchpoint_applications: number;
  live_non_event_not_reviewed_touchpoint_applications: number;
  live_non_event_touchpoint_applications_status_mapping: {
    applied: number;
    accepted: number;
    interviewing: number;
    hired: number;
    rejected: number;
  };
  following_count: number;
  views_total: number;
  views_today: number;
  views_in_last_7: number;

  views_in_last_30: number;

  event_registrations: number;
  total_count_map: {
    Internship: number;
    Event: number;
    Job: number;
  };
  live_count_map: {
    Internship: number;
    Event: number;
    Job: number;
  };
  closed_count_map: {
    Internship: number;
    Event: number;
    Job: number;
  };
  unread_messages_count: number;
  live_event_touchpoint_applications_avatar_url: Array<AvatarURLsAttributes>;
  live_non_reviewed_not_event_avatar_url: Array<AvatarURLsAttributes>;
  not_reviewed_not_events_touchpoints: Array<{
    id: string;
    title: string;
    touchpointable_type: TouchpointType;
    not_reviewed_application_count: number;
  }>;
  live_events: Array<{
    id: string;
    title: string;
    touchpointable_type: TouchpointType;
  }>;
  event_status_count_mapping: {
    published: number;
    draft: number;
    ready_for_approval: number;
    expired: number;
  };
  non_event_status_count_mapping: {
    published: number;
    draft: number;
    ready_for_approval: number;
    expired: number;
  };
  profile_complete: boolean;
  response_rate_verified: boolean;
}
